import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Check, Users, ShoppingBag, TrendingUp, Wallet, MousePointer, Eye, CreditCard, BarChart3, Calendar, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { encodeShareId } from '../lib/shareCode';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiFetchJson } from '../lib/api';
import { formatPrice, maskPhone } from '../lib/utils';
import { trackPageView } from '../lib/analytics';
import type { ReferralDashboardData } from '../types';

// 夥伴 Dashboard——雙 TAB：
//   🎁 辣友卡會員（個人福利：QR 換領／推廣卡）｜💰 推薦賺回贈（業務：連結＋流量＋佣金）
// 兩件事分開呈現——會員換領嗰陣唔會撞見推薦漏斗。?tab=referral 可深連結直達。

const STATUS_LABELS: Record<string, string> = {
  pending: '待確認',
  approved: '已確認',
  paid: '已發放',
  cancelled: '已取消',
};

const DAY_OPTIONS = [
  { value: 7, label: '最近 7 天' },
  { value: 30, label: '最近 30 天' },
  { value: 90, label: '最近 90 天' },
];

type TabKey = 'member' | 'referral';

function initialTab(): TabKey {
  return new URLSearchParams(window.location.search).get('tab') === 'referral' ? 'referral' : 'member';
}

/** 推廣卡 CTA 文案——打字機逐字打出，完成後彈出辣椒 */
const PROMO_CTA_TEXT = '立即加入，開始每月免費攞 →';

export default function ReferralDashboard() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ReferralDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<TabKey>(initialTab);

  // 推廣卡 CTA 打字機（只喺非會員推廣卡出現時跑；完成後彈辣椒）
  // hooks 必須喺 early return 之前——否則 loading→loaded 轉場 hooks 數量突變即炸
  const showPromo = !!data && !!data.wanla && !data.wanla.isMember;
  const [ctaTyped, setCtaTyped] = useState(0);
  const [ctaDone, setCtaDone] = useState(false);
  useEffect(() => {
    if (!showPromo) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setCtaTyped(PROMO_CTA_TEXT.length);
      setCtaDone(true);
      return;
    }
    let timer: ReturnType<typeof setInterval> | undefined;
    let pop: ReturnType<typeof setTimeout> | undefined;
    const start = setTimeout(() => {
      let i = 0;
      timer = setInterval(() => {
        i += 1;
        setCtaTyped(i);
        if (i >= PROMO_CTA_TEXT.length) {
          if (timer) clearInterval(timer);
          pop = setTimeout(() => setCtaDone(true), 300);
        }
      }, 85);
    }, 450);
    return () => { clearTimeout(start); if (timer) clearInterval(timer); if (pop) clearTimeout(pop); };
  }, [showPromo]);

  useEffect(() => {
    trackPageView('/ref/d/:token');
    if (!token) {
      setLoading(false);
      return;
    }
    apiFetchJson<ReferralDashboardData>(`/public/referral/dashboard/${token}?days=${days}`)
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error || '找不到夥伴資料');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('載入失敗');
        setLoading(false);
      });
  }, [token, days]);

  const switchTab = (t: TabKey) => {
    setTab(t);
    const url = new URL(window.location.href);
    if (t === 'referral') url.searchParams.set('tab', 'referral');
    else url.searchParams.delete('tab');
    window.history.replaceState(null, '', url.toString());
  };

  const handleCopy = async () => {
    if (!data?.referralLink) return;
    await navigator.clipboard.writeText(data.referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">載入中...</div>
        <Footer />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{error || '無效的連結'}</h1>
        </div>
        <Footer />
      </div>
    );
  }

  const wanlaEligible = data.wanla?.isMember && data.wanla.eligibleNow;
  const pendingReward = data.pendingCommission || 0;
  const waShareText = encodeURIComponent(`🌶️ 我喺香港辣谷搵到好多勁嘢！用我個連結落單，大家都有著數：${data.referralLink}`);

  const tabBtn = (key: TabKey, label: string, badge: React.ReactNode) => (
    <button
      onClick={() => switchTab(key)}
      className={`flex items-center justify-center gap-1.5 py-3.5 px-2 text-sm sm:text-base font-bold border-b-[3px] transition-colors ${
        tab === key
          ? 'border-orange-600 text-orange-700 bg-orange-50/60'
          : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'
      }`}
    >
      {label}
      {badge}
    </button>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero（收窄＋問候合併——手機開屏即見內容） */}
      <section className="bg-x1493-dark text-white pt-8 pb-6">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-2xl md:text-3xl font-bold">{data.name}，你好！👋</h1>
          <p className="text-white/70 text-sm mt-1">香港辣谷夥伴 Dashboard · 歡迎返嚟辣味家族</p>
        </div>
      </section>

      {/* Sticky TAB bar */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-stone-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 grid grid-cols-2">
          {tabBtn('member', '🎁 辣友卡會員', wanlaEligible ? (
            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">✓ 可領</span>
          ) : null)}
          {tabBtn('referral', '💰 推薦賺回贈', pendingReward > 0 ? (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">HK${Math.round(pendingReward / 100)}</span>
          ) : null)}
        </div>
      </div>

      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 space-y-8">
          {/* ================= TAB：辣友卡會員（個人福利） ================= */}
          {tab === 'member' && (
            <>
              {data.wanla && !data.wanla.isMember && (() => {
                const buyLink = data.wanla!.productId ? `/p/${encodeShareId(data.wanla!.productId)}` : '/store';
                return (
                  <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <h3 className="text-lg font-bold flex items-center gap-2">🌶️ 辣友卡·年費會員</h3>
                      <span className="text-xs bg-white/20 px-2 py-1 rounded-full font-bold">HK${((data.wanla!.priceCents || 9900) / 100).toFixed(0)}/年</span>
                    </div>
                    <p className="text-white/90 text-sm font-medium">你仲未係會員——每月免費攞「辣中聖品」嘅著數，你仲未歎到！</p>
                    <ul className="mt-3 space-y-1.5 text-sm text-white/95">
                      <li>⏳ 即時生效——買卡當日就去任何參與店鋪免費攞第一盒（價值HK$58）</li>
                      <li>📅 之後每 30 日多一盒，一年 12 盒（總值HK$696）</li>
                      <li>🏆 領足 12 次仲有年度神秘辣禮🎁</li>
                    </ul>
                    <a
                      href={buyLink}
                      aria-label={PROMO_CTA_TEXT}
                      className="relative mt-4 block sm:inline-block w-full sm:w-auto text-center bg-white text-orange-700 font-bold px-6 py-3 rounded-xl hover:bg-orange-50 transition"
                    >
                      {/* 隱形全文撐住固定寬度——打字過程版面唔會拉扯 */}
                      <span className="invisible" aria-hidden="true">{PROMO_CTA_TEXT}🌶️</span>
                      <span className="absolute inset-0 flex items-center justify-center gap-1 px-6">
                        <span>{PROMO_CTA_TEXT.slice(0, ctaTyped)}</span>
                        {!ctaDone && ctaTyped < PROMO_CTA_TEXT.length && (
                          <span className="inline-block w-[3px] h-[1.1em] bg-orange-700 animate-pulse align-middle" />
                        )}
                        <span className={`chili-fx ${ctaDone ? 'on' : ''}`} aria-hidden="true">🌶️</span>
                      </span>
                    </a>
                    <p className="text-white/75 text-xs mt-3">既然喺度買開嘢，就冇理由唔做辣友卡會員啦 😉</p>
                  </div>
                );
              })()}
              {data.wanla?.isMember && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-orange-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2">🌶️ 辣友卡·年費會員</h3>
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">有效期至 {new Date(data.wanla.expireAt! * 1000).toLocaleDateString('zh-HK')}</span>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <div className="flex flex-col sm:flex-row items-center gap-5">
                      {data.wanla.memberLink && (
                        <div className="text-center flex-none">
                          <div className="inline-block border-4 border-orange-200 rounded-2xl p-2.5 bg-white">
                            <QRCodeSVG value={data.wanla.memberLink} size={150} level="M" />
                          </div>
                          <p className="text-xs text-stone-500 mt-1.5 font-medium">到店出示俾店員掃 📷</p>
                        </div>
                      )}
                      <div className="flex-1 min-w-[180px] text-center sm:text-left">
                        <p className="text-sm text-stone-600">專屬會員碼：</p>
                        <p className="font-mono text-2xl font-bold tracking-widest text-orange-600 my-1">{data.wanla.memberCode || '—'}</p>
                        {data.wanla.eligibleNow ? (
                          <span className="inline-block text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-bold">✓ 現在可換領——去任何參與店鋪</span>
                        ) : (
                          <span className="inline-block text-xs bg-stone-100 text-stone-500 px-3 py-1.5 rounded-full">下次可領：{data.wanla.nextEligibleAt ? new Date(data.wanla.nextEligibleAt * 1000).toLocaleDateString('zh-HK') : '—'}</span>
                        )}
                        <p className="text-sm text-stone-600 mt-2">免費換領：{data.wanla.perk}</p>
                        {data.wanla.memberLink && (
                          <a href={data.wanla.memberLink} target="_blank" rel="noreferrer" className="inline-block text-xs text-orange-600 hover:underline mt-2">開啟換領頁（店鋪清單＋店員核銷）↗</a>
                        )}
                      </div>
                    </div>
                    {(data.wanla.claimsHistory?.length || 0) > 0 && (
                      <div className="mt-3 border-t border-orange-100 pt-2">
                        <p className="text-xs text-stone-500 mb-1">換領記錄（最近）：</p>
                        {data.wanla.claimsHistory!.slice(0, 5).map((h, i) => (
                          <p key={i} className="text-xs text-stone-600">🏪 {h.shopName || '店鋪'} · {new Date(h.claimedAt * 1000).toLocaleDateString('zh-HK')}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-stone-500 mt-3">已換領 {data.wanla.claimedCount}/12 · 每 30 日一盒 · 領足 12 次有年度神秘辣禮 🌶️</p>
                </div>
              )}
              {!data.wanla && (
                <div className="bg-stone-50 border border-stone-200 rounded-2xl p-8 text-center text-stone-500 text-sm">
                  辣友卡會員服務即將開放——密切留意 🌶️
                </div>
              )}
            </>
          )}

          {/* ================= TAB：推薦賺回贈（業務） ================= */}
          {tab === 'referral' && (
            <>
              {/* 專屬連結＋分享 */}
              <div className="bg-stone-50 rounded-2xl p-6">
                <h3 className="font-bold text-lg mb-3">你的專屬連結</h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    readOnly
                    value={data.referralLink}
                    className="flex-1 p-3 border border-stone-200 rounded-xl bg-white text-stone-600 text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopy}
                      className="flex-1 sm:flex-none bg-x1493-dark text-white px-4 py-2 rounded-xl font-bold hover:bg-black transition flex items-center justify-center gap-2"
                    >
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                      {copied ? '已複製' : '複製'}
                    </button>
                    <a
                      href={`https://wa.me/?text=${waShareText}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 sm:flex-none bg-[#25D366] text-white px-4 py-2 rounded-xl font-bold hover:brightness-105 transition flex items-center justify-center gap-2"
                    >
                      <Share2 size={18} />
                      WhatsApp 分享
                    </a>
                  </div>
                </div>
                <p className="text-xs text-stone-500 mt-2">朋友經呢條連結落單，你賺 10% 回贈 💰</p>
              </div>

              {/* 佣金統計 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-stone-50 rounded-2xl p-5 text-center">
                  <Users className="mx-auto text-x1493-secondary mb-2" size={28} />
                  <p className="text-2xl font-bold">{data.totalBoundCustomers}</p>
                  <p className="text-xs text-stone-500">綁定顧客</p>
                </div>
                <div className="bg-stone-50 rounded-2xl p-5 text-center">
                  <ShoppingBag className="mx-auto text-x1493-secondary mb-2" size={28} />
                  <p className="text-2xl font-bold">{data.totalOrders}</p>
                  <p className="text-xs text-stone-500">成交訂單</p>
                </div>
                <div className="bg-stone-50 rounded-2xl p-5 text-center">
                  <TrendingUp className="mx-auto text-x1493-secondary mb-2" size={28} />
                  <p className="text-2xl font-bold">{data.approvedCommission}</p>
                  <p className="text-xs text-stone-500">已確認回贈（分）</p>
                </div>
                <div className="bg-stone-50 rounded-2xl p-5 text-center">
                  <Wallet className="mx-auto text-x1493-secondary mb-2" size={28} />
                  <p className="text-2xl font-bold">{data.paidCommission}</p>
                  <p className="text-xs text-stone-500">已發放回贈（分）</p>
                </div>
              </div>

              {/* 流量與轉化 */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <BarChart3 className="text-x1493-secondary" size={24} />
                    流量與轉化
                  </h3>
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-stone-500" />
                    <select
                      value={days}
                      onChange={(e) => setDays(Number(e.target.value))}
                      className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm bg-white text-stone-700 focus:ring-2 focus:ring-x1493-primary outline-none"
                    >
                      {DAY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-stone-50 rounded-xl p-4 text-center">
                    <MousePointer className="mx-auto text-x1493-secondary mb-1" size={22} />
                    <p className="text-xl font-bold">{data.traffic?.clicks ?? 0}</p>
                    <p className="text-xs text-stone-500">連結獨立點擊</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4 text-center">
                    <Eye className="mx-auto text-x1493-secondary mb-1" size={22} />
                    <p className="text-xl font-bold">{data.traffic?.uniqueVisitors ?? 0}</p>
                    <p className="text-xs text-stone-500">獨立訪客</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4 text-center">
                    <ShoppingBag className="mx-auto text-x1493-secondary mb-1" size={22} />
                    <p className="text-xl font-bold">{data.traffic?.orders ?? 0}</p>
                    <p className="text-xs text-stone-500">成交訂單</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-4 text-center">
                    <CreditCard className="mx-auto text-x1493-secondary mb-1" size={22} />
                    <p className="text-xl font-bold">{formatPrice(data.traffic?.revenue ?? 0)}</p>
                    <p className="text-xs text-stone-500">帶來營收</p>
                  </div>
                </div>

                {/* 轉化漏斗 */}
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-stone-700 mb-3">轉化漏斗</h4>
                  <div className="space-y-2">
                    {[
                      { label: '獨立點擊', value: data.traffic?.clicks ?? 0, color: 'bg-blue-500' },
                      { label: '瀏覽頁面', value: data.traffic?.pageViews ?? 0, color: 'bg-indigo-500' },
                      { label: '加入購物車', value: data.traffic?.addToCarts ?? 0, color: 'bg-purple-500' },
                      { label: '開始結帳', value: data.traffic?.beginCheckouts ?? 0, color: 'bg-pink-500' },
                      { label: '完成訂單', value: data.traffic?.orders ?? 0, color: 'bg-green-500' },
                    ].map((step, idx, arr) => {
                      const max = Math.max(1, arr[0].value);
                      const prev = idx > 0 ? arr[idx - 1].value : step.value;
                      const rate = prev ? ((step.value / prev) * 100).toFixed(1) + '%' : '0%';
                      return (
                        <div key={step.label} className="flex items-center gap-3">
                          <div className="w-24 text-xs text-stone-500 text-right">{step.label}</div>
                          <div className="flex-1 h-8 bg-stone-100 rounded-lg overflow-hidden relative">
                            <div
                              className={`h-full ${step.color} transition-all duration-500`}
                              style={{ width: `${Math.min(100, (step.value / max) * 100)}%` }}
                            />
                            <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-stone-700">
                              {step.value.toLocaleString('zh-HK')} {idx > 0 && <span className="ml-2 text-stone-400">({rate})</span>}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm text-stone-600">
                    <span>整體點擊轉化率：<span className="font-bold text-x1493-primary">{data.traffic?.clickToOrderRate ?? '0%'}</span></span>
                    <span>訪客轉化率：<span className="font-bold text-x1493-primary">{data.traffic?.visitorToOrderRate ?? '0%'}</span></span>
                  </div>
                </div>

                {/* 熱門頁面 */}
                {data.topPages && data.topPages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-stone-700 mb-3">熱門頁面</h4>
                    <div className="space-y-2">
                      {data.topPages.map((page, idx) => {
                        let displayPage = page.page || '/';
                        try { displayPage = decodeURIComponent(displayPage); } catch { /* keep original */ }
                        return (
                          <div key={page.page + idx} className="flex items-center justify-between text-sm">
                            <span className="text-stone-600 truncate max-w-[70%]">{displayPage}</span>
                            <span className="font-medium text-stone-800">{page.views} 次瀏覽</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* 兌換提示 */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-center">
                <p className="text-amber-800 font-medium">消費的10%即成回贈，兌換請 WhatsApp 聯絡客服 🌶️—— 自用變相九折，分享持續回贈😉</p>
              </div>

              {/* 最近成交 */}
              <div>
                <h3 className="text-xl font-bold mb-4">最近成交紀錄</h3>
                {data.recentOrders.length === 0 ? (
                  <p className="text-stone-500">暫無成交紀錄</p>
                ) : (
                  <div className="space-y-3">
                    {data.recentOrders.map((order) => (
                      <div key={order.orderNum} className="bg-stone-50 rounded-xl p-4 flex flex-col sm:flex-row justify-between gap-2">
                        <div>
                          <p className="font-bold">#{order.orderNum}</p>
                          <p className="text-sm text-stone-500">{maskPhone(order.customerPhone)}</p>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="font-bold text-x1493-primary">{formatPrice(order.orderAmount)}</p>
                          <p className="text-sm text-stone-600">回贈 {order.commissionPoints} 分</p>
                          <span className="inline-block mt-1 bg-white px-2 py-0.5 rounded text-xs font-bold text-stone-600">
                            {STATUS_LABELS[order.status] || order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
